import React, { useState, useEffect } from 'react';
import { Trash2, Mail, MailOpen, Search, Calendar, Archive, Inbox } from 'lucide-react';
import { loadContactMessages, markMessageAsRead, deleteContactMessage } from '../../services/contactService';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  purpose: string;
  message: string;
  timestamp: string;
  read: boolean;
}

interface AdminInboxProps {
  theme: 'light' | 'dark';
}

export const AdminInbox: React.FC<AdminInboxProps> = ({ theme }) => {
  const isDark = theme === 'dark';
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPurpose, setFilterPurpose] = useState<string>('all');
  const [filterRead, setFilterRead] = useState<'all' | 'unread' | 'read'>('all');

  // Load messages from Supabase/localStorage
  useEffect(() => {
    let isMounted = true;
    
    const loadMessages = async () => {
      if (!isMounted) return;
      try {
        const loadedMessages = await loadContactMessages();
        if (isMounted) {
          console.log(`📊 AdminInbox: Loaded ${loadedMessages.length} messages`);
          setMessages(loadedMessages);
        }
      } catch (err) {
        console.error('❌ Failed to load messages in AdminInbox:', err);
      }
    };
    
    // Load immediately on mount
    loadMessages();
    
    const pollInterval = setInterval(loadMessages, 2000);

    // Listen for custom event when new message is sent from landing page
    const handleNewMessage = (event: Event) => {
      console.log('🔔 New message event received in AdminInbox');
      loadMessages();
    };
    
    window.addEventListener('contact_message_sent', handleNewMessage);
    
    return () => {
      isMounted = false;
      window.removeEventListener('contact_message_sent', handleNewMessage);
      clearInterval(pollInterval);
    };
  }, []);

  const filteredMessages = messages.filter(msg => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      msg.name.toLowerCase().includes(searchLower) ||
      msg.email.toLowerCase().includes(searchLower) ||
      msg.message.toLowerCase().includes(searchLower);

    // Purpose filter
    const matchesPurpose = filterPurpose === 'all' || msg.purpose === filterPurpose;

    // Read filter
    const matchesRead = 
      filterRead === 'all' || 
      (filterRead === 'unread' && !msg.read) ||
      (filterRead === 'read' && msg.read);

    return matchesSearch && matchesPurpose && matchesRead;
  });

  const handleMarkAsRead = async (id: string) => {
    await markMessageAsRead(id);
    setMessages(msgs => {
      const updated = msgs.map(m => 
        m.id === id ? { ...m, read: true } : m
      );
      return updated;
    });
  };

  const handleDelete = async (id: string) => {
    await deleteContactMessage(id);
    setMessages(msgs => {
      const updated = msgs.filter(m => m.id !== id);
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
      return updated;
    });
  };

  const handleSelectMessage = (msg: ContactMessage) => {
    setSelectedMessage(msg);
    if (!msg.read) {
      handleMarkAsRead(msg.id);
    }
  };

  const uniquePurposes = Array.from(new Set(messages.map(m => m.purpose)));
  const unreadCount = messages.filter(m => !m.read).length;

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className={`flex flex-col h-full ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'} p-4 sm:p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isDark ? 'bg-red-500/20' : 'bg-red-100'
            }`}>
              <Inbox className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                Messages Inbox
              </h1>
              {unreadCount > 0 && (
                <p className="text-sm text-red-500 font-medium">
                  {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full font-bold ${
            isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-700'
          }`}>
            {messages.length}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-3">
          <div className="relative">
            <Search className={`absolute left-3 top-3 w-5 h-5 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`} />
            <input
              type="text"
              placeholder="Search by name, email, or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none'
                  : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-500 focus:border-red-500 focus:outline-none'
              }`}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Read filter */}
            <select
              value={filterRead}
              onChange={(e) => setFilterRead(e.target.value as 'all' | 'unread' | 'read')}
              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                isDark
                  ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                  : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
              }`}
            >
              <option value="all">All Messages</option>
              <option value="unread">Unread Only</option>
              <option value="read">Read Messages</option>
            </select>

            {/* Purpose filter */}
            <select
              value={filterPurpose}
              onChange={(e) => setFilterPurpose(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                isDark
                  ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                  : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
              }`}
            >
              <option value="all">All Purposes</option>
              {uniquePurposes.map(purpose => (
                <option key={purpose} value={purpose}>{purpose}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Messages List */}
        <div className={`w-full md:w-96 border-r ${isDark ? 'border-zinc-800' : 'border-zinc-200'} overflow-y-auto`}>
          {filteredMessages.length === 0 ? (
            <div className={`h-full flex flex-col items-center justify-center p-6 text-center ${
              isDark ? 'bg-zinc-900' : 'bg-zinc-50'
            }`}>
              <Mail className={`w-12 h-12 mb-3 ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
              <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                {searchTerm || filterPurpose !== 'all' ? 'No messages match your filters' : 'No messages yet'}
              </p>
            </div>
          ) : (
            <div>
              {filteredMessages.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => handleSelectMessage(msg)}
                  className={`w-full px-4 py-3 sm:px-6 sm:py-4 border-b transition-colors text-left ${
                    selectedMessage?.id === msg.id
                      ? isDark
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-red-50 border-red-300'
                      : isDark
                      ? 'bg-zinc-900 border-zinc-800 hover:bg-zinc-800'
                      : 'bg-white border-zinc-200 hover:bg-zinc-50'
                  }`}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    {msg.read ? (
                      <MailOpen className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-1 ${
                        isDark ? 'text-zinc-600' : 'text-zinc-400'
                      }`} />
                    ) : (
                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-1 text-red-500" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 justify-between">
                        <p className={`font-semibold text-sm sm:text-base truncate ${
                          isDark ? 'text-white' : 'text-zinc-900'
                        }`}>
                          {msg.name}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded whitespace-nowrap flex-shrink-0 ${
                          isDark
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {msg.purpose}
                        </span>
                      </div>
                      <p className={`text-xs sm:text-sm truncate mt-1 ${
                        isDark ? 'text-zinc-400' : 'text-zinc-600'
                      }`}>
                        {msg.email}
                      </p>
                      <p className={`text-xs sm:text-sm truncate mt-1 ${
                        isDark ? 'text-zinc-500' : 'text-zinc-500'
                      }`}>
                        {msg.message}
                      </p>
                      <p className={`text-xs mt-2 flex items-center gap-1 ${
                        isDark ? 'text-zinc-600' : 'text-zinc-500'
                      }`}>
                        <Calendar className="w-3 h-3" />
                        {formatDate(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className={`hidden md:flex md:w-full flex-col overflow-hidden ${
          isDark ? 'bg-zinc-900' : 'bg-zinc-50'
        }`}>
          {selectedMessage ? (
            <>
              {/* Detail Header */}
              <div className={`border-b ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'} p-6`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                      {selectedMessage.name}
                    </h2>
                    <p className="text-red-500 font-mono text-sm mt-1">{selectedMessage.email}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isDark
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {selectedMessage.purpose}
                      </span>
                      <span className={`flex items-center gap-1 text-sm ${
                        isDark ? 'text-zinc-400' : 'text-zinc-600'
                      }`}>
                        <Calendar className="w-4 h-4" />
                        {new Date(selectedMessage.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(selectedMessage.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      isDark
                        ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                        : 'bg-red-100 hover:bg-red-200 text-red-600'
                    }`}
                    title="Delete message"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Detail Body */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}>
                  <div className={`whitespace-pre-wrap text-sm sm:text-base leading-relaxed ${
                    isDark ? 'text-zinc-300' : 'text-zinc-700'
                  }`}>
                    {selectedMessage.message}
                  </div>
                </div>
              </div>

              {/* Detail Footer */}
              <div className={`border-t ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'} p-4 flex gap-2`}>
                <button
                  onClick={() => {
                    const email = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.purpose}`;
                    window.location.href = email;
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Reply
                </button>
                <button
                  onClick={() => handleDelete(selectedMessage.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center">
              <Mail className={`w-16 h-16 mb-4 ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
              <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                Select a message to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
